import Promise from 'bluebird';
import { PopoverView } from '@bingads-webui/popover';
import { getPopupPosition } from './get-popup-position';

/**
 * Show popup with provided template in the most suitable position
 * To close the popup, you can either click the close icon or outside of document
 *  or trigger remove event for the contentView
 *
 * @param {Object} param - An object containing these default options:
 * @param {Object} param.container - the place where to render the popup, required
 * @param {function} param.contentView - inner content, must be deterministic view
 *   you can use following ways to generate deterministic view:
 *   for template, please use :
 *   { DeterministicViewForHtml } from '@bingads-webui/deterministic-wrapper-view'.
 *  for backbone view, please use :
 *  { DeterministicViewForBackBoneView } from '@bingads-webui/deterministic-wrapper-view'
 * @param {PositioningMethod} param.positioningMethod - which method to use to
 * set popup position, default to be PositioningMethod.fit
 * @param {Object} param.positioningOptions - params for positioning
 * @param {number} param.positioningOptions.anchorPosition - used to replace position() for anchor
 *  {height, width, top, left}
 * @param {number} param.positioningOptions.threshold - used in PositionMethod.fit only. Optional.
 *  Specifies the threshold for acceptable measurement of whether the component fits
 *  inside the container or not. It is specified in relative units [0..1].
 *  1 means 100% of the component's area must fit inside the container;
 *  0 means 0% can fit and position will be acceptable. If threshold is not specified, 1 is assumed
 *  same format as backbone event settings
 * @param {String[]} param.classes - custom classes for popup container
 * @param {number} param.positioningOptions.location - used in PositionMethod.fixed only.
 *  Required in fixed option, it can be any value of the { FixedPositionLocation }
 * @param {jQuery} param.anchor - the element as position base for popup  position
 *
 * @returns {Promise} promise which will trigger resolve after click or save
 */
export function showPopup({
  container,
  contentView,
  positioningMethod,
  positioningOptions,
  classes,
  anchor,
  resolveOnceRendered = false,
  positioningClass,
}) {
  const getPositionAutomatically = (view) => {
    const { left, top } = getPopupPosition(
      positioningMethod,
      positioningOptions, container, anchor, view.$el
    );
    return {
      isAbsolute: true,
      left,
      top,
    };
  };

  return new Promise((resolveCallback) => {
    const options = {
      target: anchor,
      position: getPositionAutomatically,
      containerClassName: classes,
    };
    const currentPopup = new PopoverView(options);

    currentPopup.setContent(contentView);

    if (positioningClass) currentPopup.$el.addClass(positioningClass);

    if (resolveOnceRendered) {
      contentView.on('remove', () => {
        currentPopup.off('removed');
        currentPopup.remove();
      });
      resolveCallback(currentPopup);
    } else {
      currentPopup.once('removed', () => {
        resolveCallback();
      });

      contentView.on('remove', (result) => {
        resolveCallback(result);
        currentPopup.off('removed');
        currentPopup.remove();
      });
    }
    container.append(currentPopup.el);
  });
}
